import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Stack,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  InputAdornment,
  Chip,
  Grid,
  Switch,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import CategoryIcon from "@mui/icons-material/Category";
import { toast, ToastContainer } from "react-toastify";
import { useFormik } from "formik";
import api from "../../axiosInstance";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import MentorshipValidation from "../Validations/MentorshipValidation";

const ACCENT = "#17479e";

const whiteSortLabelSx = {
  color: "white !important",
  "&.Mui-active": { color: "white !important" },
  "&:hover": { color: "white !important" },
  "& .MuiTableSortLabel-icon": { color: "white !important" },
};

const columns = [
  { key: "code", label: "Code", minWidth: 140 },
  { key: "name", label: "Name", minWidth: 160 },
  { key: "description", label: "Description", minWidth: 220 },
  { key: "durationMinutes", label: "Duration", minWidth: 100, align: "center" },
  { key: "maxParticipants", label: "Capacity", minWidth: 100, align: "center" },
  {
    key: "priceAmount",
    label: "Price [Apple Users]",
    minWidth: 150,
    align: "center",
  },
  { key: "minPrice", label: "Min Price", minWidth: 110, align: "center" },
  { key: "maxPrice", label: "Max Price", minWidth: 110, align: "center" },
  { key: "productId", label: "Product ID [Apple Users]", minWidth: 200 },
  { key: "isActive", label: "Status", minWidth: 100, align: "center" },
];

const priceCellColors = {
  price: { color: "#17479e", bg: "#e3f2fd" },
  min: { color: "#6a1b9a", bg: "#f3e5f5" },
  max: { color: "#2e7d32", bg: "#e8f5e9" },
};

function PriceCell({ value, currency, variant }) {
  if (value == null)
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  const cfg = priceCellColors[variant] ?? priceCellColors.price;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        px: 1.2,
        py: 0.4,
        borderRadius: "8px",
        background: cfg.bg,
      }}
    >
      <Typography
        sx={{ fontWeight: 700, fontSize: "0.82rem", color: cfg.color }}
      >
        {currency} {value}
      </Typography>
    </Box>
  );
}

function MentorSessionTypes() {
  const [sessionTypes, setSessionTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");

  const load = () => {
    setIsLoading(true);
    api
      .get("/admin/mentor-session-types")
      .then((res) => {
        const raw = res.data?.data;
        setSessionTypes(Array.isArray(raw) ? raw : (raw?.content ?? []));
      })
      .catch((err) => {
        console.error("Session types fetch error:", err);
        setError("Failed to load session types.");
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = sessionTypes.filter(
    (s) =>
      (s.name || "").toLowerCase().includes(search.toLowerCase()) ||
      (s.code || "").toLowerCase().includes(search.toLowerCase()),
  );

  const sortedData = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === "string") {
        return order === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      if (typeof aValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }
      return 0;
    });
  }, [filtered, orderBy, order]);

  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };

  const openAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setDialogOpen(true);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      code: editing?.code || "",
      name: editing?.name || "",
      description: editing?.description || "",
      durationMinutes: editing?.durationMinutes ?? "",
      maxParticipants: editing?.maxParticipants ?? "",
      priceAmount: editing?.priceAmount ?? "",
      productId: editing?.productId || "",
      capacityMin: editing?.capacityMin ?? 1,
      lockInMinutesBeforeStart: editing?.lockInMinutesBeforeStart ?? "",
      minSessionsPerMonth: editing?.minSessionsPerMonth ?? 0,
      minPrice: editing?.minPrice ?? "",
      maxPrice: editing?.maxPrice ?? "",
      isActive: editing?.isActive ?? true,
    },
    validationSchema: MentorshipValidation.SessionTypeValidationSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      const payload = {
        code: values.code.trim(),
        name: values.name.trim(),
        description: values.description.trim(),
        durationMinutes: Number(values.durationMinutes),
        maxParticipants: Number(values.maxParticipants),
        priceAmount: Number(values.priceAmount),
        productId: values.productId.trim(),
        capacityMin: Number(values.capacityMin),
        lockInMinutesBeforeStart: Number(values.lockInMinutesBeforeStart),
        minSessionsPerMonth: Number(values.minSessionsPerMonth) || 0,
        minPrice: Number(values.minPrice),
        maxPrice: Number(values.maxPrice),
        isActive: values.isActive,
      };
      try {
        if (editing?.id) {
          await api.put(`/admin/mentor-session-types/${editing.id}`, payload);
          toast.success("Session type updated Successfully!");
        } else {
          await api.post(`/admin/mentor-session-types`, payload);
          toast.success("Session type created Successfully!");
        }
        setDialogOpen(false);
        resetForm();
        load();
      } catch (err) {
        toast.error(
          err.response?.data?.errorMessage ||
            "Failed to save session type. Please try again.",
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/mentor-session-types/${deleteId}`);
      toast.success("Session type deleted!");
      load();
    } catch (err) {
      toast.error(
        err.response?.data?.errorMessage ||
          "Failed to delete session type. Please try again.",
      );
    } finally {
      setDeleteId(null);
    }
  };

  const field = (name, label, extra = {}) => ({
    name,
    label,
    value: formik.values[name],
    onChange: formik.handleChange,
    onBlur: formik.handleBlur,
    error: formik.touched[name] && Boolean(formik.errors[name]),
    helperText: formik.touched[name] && formik.errors[name],
    fullWidth: true,
    sx: { "& .MuiOutlinedInput-root": { borderRadius: 2 } },
    ...extra,
  });

  return (
    <Box sx={{ textAlign: "left" }}>
      <ToastContainer />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ boxShadow: 1, mb: 2.5 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <TextField
              size="small"
              placeholder="Search by name or code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "#999" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 260 }}
            />
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {sortedData.length} of {sessionTypes.length} session types
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={openAdd}
                {...BUTTON_STYLES.primary}
              >
                Add Session Type
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Paper
          sx={{
            width: "100%",
            overflow: "hidden",
            boxShadow: 2,
            borderRadius: 3,
          }}
        >
          <TableContainer sx={{ maxHeight: "calc(100vh - 320px)" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {columns.map((c) => (
                    <TableCell
                      key={c.key}
                      align={c.align}
                      sx={{
                        minWidth: c.minWidth,
                        padding: "10px 12px",
                        backgroundColor: ACCENT,
                        color: "white",
                        fontWeight: 600,
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                      }}
                      sortDirection={orderBy === c.key ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === c.key}
                        direction={orderBy === c.key ? order : "asc"}
                        onClick={() => handleSort(c.key)}
                        sx={whiteSortLabelSx}
                      >
                        {c.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell
                    align="center"
                    sx={{
                      minWidth: 110,
                      padding: "10px 12px",
                      backgroundColor: ACCENT,
                      color: "white",
                      fontWeight: 600,
                      position: "sticky",
                      top: 0,
                      right: 0,
                      zIndex: 4,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + 1}
                      sx={{ textAlign: "center", py: 4 }}
                    >
                      <Typography variant="body1" color="text.secondary">
                        No session types found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedData.map((row) => {
                    const currency = row.currencyCode || "INR";
                    return (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{
                          "&:nth-of-type(odd)": {
                            backgroundColor: "rgba(0,0,0,0.02)",
                          },
                        }}
                      >
                        <TableCell sx={{ padding: "10px 12px" }}>
                          <Chip
                            label={row.code}
                            size="small"
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.7rem",
                              backgroundColor: "#eef1f6",
                              color: "#556",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ padding: "10px 12px" }}>
                          <Typography
                            sx={{ fontWeight: 600, fontSize: "0.85rem" }}
                          >
                            {row.name}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ padding: "10px 12px" }}>
                          <Typography variant="body2" color="text.secondary">
                            {row.description || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ padding: "10px 12px" }}>
                          {row.durationMinutes} min
                        </TableCell>
                        <TableCell align="center" sx={{ padding: "10px 12px" }}>
                          {row.maxParticipants}
                        </TableCell>
                        <TableCell align="center" sx={{ padding: "10px 12px" }}>
                          <PriceCell
                            value={row.priceAmount}
                            currency={currency}
                            variant="price"
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ padding: "10px 12px" }}>
                          <PriceCell
                            value={row.minPrice}
                            currency={currency}
                            variant="min"
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ padding: "10px 12px" }}>
                          <PriceCell
                            value={row.maxPrice}
                            currency={currency}
                            variant="max"
                          />
                        </TableCell>
                        <TableCell sx={{ padding: "10px 12px" }}>
                          {row.productId}
                        </TableCell>
                        <TableCell align="center" sx={{ padding: "10px 12px" }}>
                          <Chip
                            label={
                              row.isActive === false ? "Inactive" : "Active"
                            }
                            size="small"
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.68rem",
                              color:
                                row.isActive === false ? "#c62828" : "#2e7d32",
                              backgroundColor:
                                row.isActive === false ? "#ffebee" : "#e8f5e9",
                            }}
                          />
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            padding: "10px 12px",
                            position: "sticky",
                            right: 0,
                            backgroundColor: "#fff",
                            zIndex: 1,
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="center"
                          >
                            <Tooltip title="Edit" arrow>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => openEdit(row)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {row.isActive !== false && (
                              <Tooltip title="Delete" arrow>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setDeleteId(row.id)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Add/Edit dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <Box component="form" onSubmit={formik.handleSubmit}>
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              fontWeight: 700,
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: `linear-gradient(135deg, ${ACCENT}, #2f66c9)`,
              }}
            >
              <CategoryIcon sx={{ color: "#fff", fontSize: 18 }} />
            </Box>
            {editing ? "Edit Session Type" : "Add Session Type"}
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2.5 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  autoFocus
                  margin="dense"
                  {...field("code", "Code")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField margin="dense" {...field("name", "Name")} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  multiline
                  minRows={2}
                  {...field("description", "Description")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  type="number"
                  {...field("durationMinutes", "Duration (minutes)")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  type="number"
                  {...field("maxParticipants", "Max Participants")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  type="number"
                  {...field("capacityMin", "Minimum Capacity")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  type="number"
                  {...field(
                    "lockInMinutesBeforeStart",
                    "Lock-in (minutes before start)",
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  type="number"
                  {...field("minSessionsPerMonth", "Min Sessions / Month")}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  margin="dense"
                  type="number"
                  {...field("priceAmount", "Price [Apply Users]", {
                    InputProps: {
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                    },
                  })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  margin="dense"
                  type="number"
                  {...field("minPrice", "Min Price")}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  margin="dense"
                  type="number"
                  {...field("maxPrice", "Max Price ")}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  fullWidth
                  name="productId"
                  label="Product ID"
                  placeholder="com.sia.mentor.session.oneonone"
                  value={formik.values.productId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.productId && Boolean(formik.errors.productId)
                  }
                  helperText={
                    (formik.touched.productId && formik.errors.productId) || (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        e.g. com.sia.mentor.session.oneonone —{" "}
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ fontStyle: "italic", fontSize: "0.68rem" }}
                        >
                          change the text after the last "." to your own
                          reference
                        </Typography>
                      </Typography>
                    )
                  }
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.25,
                    borderRadius: 2,
                    border: "1px solid #eef0f4",
                    backgroundColor: "#fbfcfe",
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      Active
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Inactive session types are hidden from booking flows
                    </Typography>
                  </Box>
                  <Switch
                    checked={formik.values.isActive}
                    onChange={(e) =>
                      formik.setFieldValue("isActive", e.target.checked)
                    }
                  />
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              onClick={() => setDialogOpen(false)}
              {...BUTTON_STYLES.secondary}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={formik.isSubmitting}
              {...BUTTON_STYLES.primary}
            >
              Save
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <DeleteConfirmationDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this session type? This action cannot be undone."
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </Box>
  );
}

export default MentorSessionTypes;
